import { cx } from '@emotion/css';
import { Box } from '@mui/material';
import { MouseEvent, ReactNode } from 'react';

import { useViewOnly } from '@/contexts/InteractionParadigm.context';
import {
  GraphVizIconShape,
  GraphVizTileEmphasis,
} from '@/modules/graphViz/graphVizNodeConfig';
import { WithClasses } from '@/styles/types';

import { TILE_DEFAULT_VARIANT } from './constants';
import { useStyles, useTileClasses } from './styles';
import { getTileDefaultWidth, SemanticTileKind } from './Tile.utils';
import { TileCard } from './TileCard';
import { TileFontSizeVariant, TileVariant } from './types';

/** The typography variants that `lineTwo` can use. */
export type TileLineTwoVariant = 'h2light' | 'body-xs';

export interface TileProps {
  lineOne: ReactNode | string;
  lineTwo?: ReactNode;
  /**
   * The typography of `lineTwo`. Defaults to the large light value line of
   * the catalogue tiles, such as an amount under a title.
   */
  lineTwoVariant?: TileLineTwoVariant;
  lineThree?: ReactNode | string;
  variant?: TileVariant;
  /**
   * An opaque `#RGB` or `#RRGGBB` color. It replaces the colors of `variant`.
   * The variant still selects the default icon and the external outline.
   */
  customColor?: string;
  fontSizeVariant?: TileFontSizeVariant;
  notes?: string | ReactNode;
  isIconVariant?: boolean;
  iconShape?: GraphVizIconShape;
  tileEmphasis?: GraphVizTileEmphasis;
  /**
   * True once the user has explicitly sized this tile. When false/undefined on
   * an icon variant, the tile is treated as legacy and renders with
   * overflow:visible so pre-icon-scaling diagrams don't clip content that
   * outgrew the stored height. See useStyles for the gating logic.
   */
  dimensionsFinalized?: boolean;
  isGrouped?: boolean;
  isSelected?: boolean;
  children?: ReactNode;
  footerLabel?: string;
  classes?: WithClasses<typeof useStyles>;
  dragging?: boolean;
  hover?: boolean;
  isNewTile?: boolean;
  order?: number;
  xOffset?: number;
  yOffset?: number;
  stackY?: boolean;
  centerY?: boolean;
  width?: number | { width: 'auto'; minWidth: number; maxWidth: number };
  height?: number;
  omitFromCentering?: boolean;
  omitFromChunking?: boolean;
  inheritWidth?: boolean;
  testId?: string;
  hideNodeResizer?: boolean;
  hideNodeControls?: boolean;
  slots?: {
    FooterLabel?: ReactNode;
    Icon?: ReactNode;
  };
  kind?: SemanticTileKind;
  warningIndicator?: ReactNode | boolean;
  onDoubleClick?: (event: MouseEvent<HTMLElement>) => void;
  /**
   * When true, the tile shows all of its text lines and wraps them at the
   * tile width, so the tile height follows the text. When false or unset, the
   * tile clamps each line to two or three rows.
   */
  showFullText?: boolean;
}

export function Tile(props: TileProps) {
  const viewOnly = useViewOnly();
  const {
    variant = TILE_DEFAULT_VARIANT,
    width,
    height,
    isIconVariant = false,
    iconShape,
    tileEmphasis,
    fontSizeVariant,
    dimensionsFinalized,
    onDoubleClick,
    ...otherProps
  } = props;

  const tileClasses = useTileClasses({
    variant,
    viewOnly,
    isIconVariant,
    hasFixedWidth: typeof width === 'number' && width > 0,
    iconShape,
    tileEmphasis,
    fontSizeVariant,
    dimensionsFinalized,
    ...otherProps,
  });
  const { classes, variantClasses } = tileClasses;

  const tileCardProps = {
    ...props,
    isIconVariant,
    onDoubleClick,
    ...tileClasses,
  };

  if (variant === TileVariant.Pixel) {
    return <Box sx={{ width: '1px', height: '1px' }} />;
  }

  // Handle responsive width
  let calculatedWidth;
  let responsiveStyles = {};

  if (typeof width === 'object') {
    // For auto width, let it fit content but respect min/max constraints
    responsiveStyles = {
      width: 'auto',
      minWidth: width.minWidth,
      maxWidth: width.maxWidth,
      // Add flex-basis to help with sizing
      flexBasis: 'auto',
    };
    calculatedWidth = undefined;
  } else {
    // Use || over ?? so a width/height of 0 receives a default value
    calculatedWidth = width || getTileDefaultWidth(isIconVariant);
  }

  return (
    <div
      className={cx(variantClasses[variant].wrapper, classes.wrapper)}
      style={{
        width: calculatedWidth,
        height: height || undefined,
        ...responsiveStyles,
      }}
    >
      <TileCard {...tileCardProps} variant={variant} />
    </div>
  );
}
